/**
 * Account Configuration Snapshot - Frontend client.
 *
 * Reads runtime-effective account configuration snapshots produced by
 * user-initiated publishes that actually changed configuration.
 *
 * Endpoints (ise-srp-back, see amplify/backend.ts and account_config_snapshot_api):
 *   GET /account-config-snapshot/{jazelId}/latest
 *   GET /account-config-snapshot/{jazelId}/{correlationId}
 *
 * Response contract (200):
 *   { jazelId, correlationId, snapshot }
 *
 * Response contract (404, sparse-snapshot signals — UIs should render an
 * honest empty-state keyed off `missingReason`):
 *   { jazelId, correlationId, snapshot: null, missingReason }
 *
 *   missingReason values:
 *     - 'NO_SNAPSHOTS_YET'                   no publish has captured a snapshot yet
 *     - 'POINTER_INCONSISTENT'               pointer references a missing snapshot
 *     - 'NO_CONFIG_CHANGE_IN_THIS_PUBLISH'   publish ran but did not change config
 *     - 'READ_ERROR'                         transient backend error (status 500)
 */

export interface ConfigChanges {
  mapping?: boolean
  segmentation?: boolean
  listingRules?: boolean
  iseSettings?: boolean
  feedConditionRules?: boolean
  preProcessor?: boolean
  [key: string]: boolean | undefined
}

export interface NormalizedFeedEntry {
  feed: string
  rawValue?: unknown
  importProductFound: boolean
  hasPattern: boolean
  pattern: string | null
  options: Record<string, unknown> | null
}

export interface NormalizedPrimarySources {
  effective: string[]
  unsupported: unknown[]
  feeds: NormalizedFeedEntry[]
}

export interface NormalizedListingRules {
  fallbackBehavior: string | null
  exceptionRulesCount: number
  blockingRulesCount: number
  version: string | null
  lastUpdated: string | null
  legacyPrimarySourcesPresent: boolean
}

export interface NormalizedFieldMapping {
  mappedFieldCount: number
  version: string | null
  lastUpdated: string | null
}

export interface NormalizedSegmentation {
  segmentCount: number
  version: string | null
  lastUpdated: string | null
}

export type PreProcessorMode = 'groupSite' | 'sharedInventory' | 'none' | 'unknown'

export interface NormalizedPreProcessorSource {
  sourceJuid: string
  sourceFeed: string | null
  useSourceLocation: boolean
  filterFieldCount: number
}

export interface NormalizedPreProcessor {
  mode: PreProcessorMode
  productFound: boolean
  isActive: boolean
  sources: NormalizedPreProcessorSource[]
  feedSource: string | null
  pattern: string | null
  sharedFilterCount: number
}

export interface NormalizedAccountConfig {
  primarySources: NormalizedPrimarySources
  listingRules: NormalizedListingRules
  fieldMapping: NormalizedFieldMapping
  segmentation: NormalizedSegmentation
  preProcessor?: NormalizedPreProcessor
}

export interface RawAccountConfig {
  iseSettings: unknown | null
  fieldMapping: unknown | null
  segmentation: unknown | null
  listingRules: unknown | null
  feedConditionRules?: unknown | null
  preProcessor?: unknown | null
  importProducts: Record<string, unknown | null>
}

export interface SnapshotBuildIssue {
  productId: string
  message: string
}

export interface AccountConfigSnapshot {
  version: string
  jazelId: string
  correlationId: string
  generatedAt: string
  stage: string
  triggeredBy: ConfigChanges | null
  normalized: NormalizedAccountConfig
  raw: RawAccountConfig
  issues: SnapshotBuildIssue[]
}

export type SnapshotMissingReason =
  | 'NO_SNAPSHOTS_YET'
  | 'POINTER_INCONSISTENT'
  | 'NO_CONFIG_CHANGE_IN_THIS_PUBLISH'
  | 'READ_ERROR'

export interface AccountConfigSnapshotResponse {
  jazelId: string
  correlationId: string | null
  snapshot: AccountConfigSnapshot | null
  missingReason?: SnapshotMissingReason
  details?: string
}

function snapshotPath(jazelId: string, correlationParam: string): string {
  return `/account-config-snapshot/${encodeURIComponent(jazelId)}/${encodeURIComponent(
    correlationParam,
  )}`
}

async function readResponse(res: Response): Promise<AccountConfigSnapshotResponse> {
  // Backend returns a structured JSON body for both 200 and the sparse-missing 404s.
  // Only treat parse failures or unexpected statuses as hard errors.
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    if (!res.ok) {
      throw new Error(`HTTP ${res.status} ${res.statusText}`)
    }
    throw new Error('Invalid response from account configuration snapshot API.')
  }

  const typed = body as Partial<AccountConfigSnapshotResponse> & { error?: string }

  if (res.ok) {
    return typed as AccountConfigSnapshotResponse
  }

  // Sparse-missing 404 cases carry `missingReason` and a null snapshot.
  if (res.status === 404 && typed && typed.missingReason) {
    return typed as AccountConfigSnapshotResponse
  }

  // Backend READ_ERROR comes back as 500 with `missingReason: 'READ_ERROR'`.
  if (res.status === 500 && typed && typed.missingReason === 'READ_ERROR') {
    return typed as AccountConfigSnapshotResponse
  }

  throw new Error(typed?.error || typed?.details || `HTTP ${res.status} ${res.statusText}`)
}

export async function getLatestAccountConfigSnapshot(
  endpoint: string,
  jazelId: string,
): Promise<AccountConfigSnapshotResponse> {
  const url = `${endpoint}${snapshotPath(jazelId, 'latest')}`
  const res = await fetch(url, { method: 'GET' })
  return readResponse(res)
}

export async function getAccountConfigSnapshotByCorrelationId(
  endpoint: string,
  jazelId: string,
  correlationId: string,
): Promise<AccountConfigSnapshotResponse> {
  const url = `${endpoint}${snapshotPath(jazelId, correlationId)}`
  const res = await fetch(url, { method: 'GET' })
  return readResponse(res)
}
